import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import ErrorLayout from '../layouts/ErrorLayout';
import Landing from 'components/pages/landing/Landing';

import Error404 from 'components/errors/Error404';
import Error500 from 'components/errors/Error500';

// Used by app (profile dropdown, notifications, billing)
import Profile from 'components/pages/user/profile/Profile';
import Settings from 'components/pages/user/settings/Settings';
import Notifications from 'components/app/social/notifications/Notifications';
import Billing from 'components/app/e-commerce/billing/Billing';
import ShoppingCart from 'components/app/e-commerce/cart/ShoppingCart';

//LOGIN
import Login from 'views/login/Login';
import Logout from 'views/login/Logout';
import ForgetPassword from 'views/login/ForgetPassword';
import ConfirmMail from 'views/login/ConfirmMail';
import PasswordReset from 'views/login/PasswordReset';
import LockScreen from 'views/login/LockScreen';
import TrialExpired from 'views/login/TrialExpired';

import WizardAuth from 'views/login/wizard/WizardAuth';
import Wizard from 'views/login/wizard/Wizard';

//#endregion

import ProtectedRoute from './ProtectedRoute';
import GuestRoute from './GuestRoute';
import { useAuth } from 'context/AuthContext';
import { getDefaultWorkspaceRoute, getWorkspaceMode } from 'helpers/workspace';
import DevTesting from 'views/dev-testing/DevTesting';
import LazyRoute from 'shared/routing/LazyRoute';

import Waitlist from 'views/waitlist/Waitlist';
import WaitlistStatus from 'views/waitlist/WaitlistStatus';
import ModeDashboard from 'views/workspace/ModeDashboard';
import PartnersDashboard from 'views/partners/PartnersDashboard';
import BlogListPage from 'views/blog/BlogListPage';
import BlogEditorPage from 'views/blog/BlogEditorPage';
import SalesDashboardPage from 'views/sales/SalesDashboardPage';
import SalesPipelinePage from 'views/sales/SalesPipelinePage';
import SalesTasksPage from 'views/sales/SalesTasksPage';
import SalesAccountsToActivatePage from 'views/sales/SalesAccountsToActivatePage';
import SalesCompaniesPage from 'views/sales/SalesCompaniesPage';
import SalesCompanyProfilePage from 'views/sales/SalesCompanyProfilePage';
import SalesOpportunityDetailPage from 'views/sales/SalesOpportunityDetailPage';
import SalesPromoPage from 'views/sales/SalesPromoPage';
import SalesReportsPage from 'views/sales/SalesReportsPage';

const Agents = React.lazy(() => import('modules/agents/AgentsModule'));
const Messages = React.lazy(() => import('modules/messages/MessagesModule'));
const MercadoLibreMessages = React.lazy(() => import('modules/messages/MercadoLibreMessagesModule'));
const Integrations = React.lazy(() => import('modules/integrations/IntegrationsModule'));
const Products = React.lazy(() => import('modules/posts/ProductsModule'));
const Stock = React.lazy(() => import('modules/stock/StockModule'));
const Questions = React.lazy(() => import('modules/questions/QuestionsModule'));
const Sales = React.lazy(() => import('modules/sales/SalesModule'));
const Analisis = React.lazy(() => import('modules/sales/AnalisisModule'));
const Claims = React.lazy(() => import('modules/claims/ClaimsModule'));
const CalendarModule = React.lazy(() => import('modules/calendar/CalendarModule'));
const Activity = React.lazy(() => import('modules/activity/ActivityModule'));
const AddProduct = React.lazy(() => import('modules/posts/AddProductModule'));
const PostProductDetails = React.lazy(() => import('modules/posts/ProductDetailsModule'));
const Users = React.lazy(() => import('modules/access/UsersModule'));
const Profiles = React.lazy(() => import('modules/access/ProfilesModule'));
const SuperAdmin = React.lazy(() => import('modules/superadmin/SuperAdminModule'));
const Subscription = React.lazy(() => import('modules/subscription/SubscriptionModule'));
const PriceCalculator = React.lazy(() => import('modules/price-calculator/PriceCalculatorModule'));

const FalconRoutes = () => {

  const { isLocked, isAuthenticated, userData } = useAuth();

  return (
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated()
            ? <Navigate to={getDefaultWorkspaceRoute(getWorkspaceMode(userData))} replace />
            : <Landing />
        }
      />
      <Route path="landing" element={<Navigate to="/" replace />} />
      <Route element={<ErrorLayout />}>
        <Route path="errors/404" element={<Error404 />} />
        <Route path="errors/500" element={<Error500 />} />
      </Route>
      {/*- ------------- Authentication ---------------------------  */}
      
      <Route
        path="login"
        element={
          <GuestRoute isAuthenticated={isAuthenticated()}>
            <Login />
          </GuestRoute>
        }
        />
      <Route
        path="login/register"
        element={<Navigate to="/waitlist" replace />}
      />
      <Route path="waitlist" element={<Waitlist />} />
      <Route path="waitlist/status" element={<WaitlistStatus />} />
      <Route path="trial-expired" element={<TrialExpired />} />
      <Route
        path="logout"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated()}>
            <Logout />
          </ProtectedRoute>
        }
      />
      <Route
        path="login/forgot-password"
        element={
          <GuestRoute isAuthenticated={isAuthenticated()}>
            <ForgetPassword />
          </GuestRoute>
        }
      />
      <Route
        path="login/reset-password"
        element={
          <GuestRoute isAuthenticated={isAuthenticated()}>
            <PasswordReset />
          </GuestRoute>
        }
      />
      <Route
        path="login/confirm-mail"
        element={
          <GuestRoute isAuthenticated={isAuthenticated()}>
            <ConfirmMail />
          </GuestRoute>
        }
      />
      <Route
        path="/lock-screen"
        element={
          <ProtectedRoute isAuthenticated={isLocked()}>
            <LockScreen />
          </ProtectedRoute>
        }
      />

      {//isAuthenticated() &&
        (//(hasIntegration('MERCADO_LIBRE'))?
        <Route element={<MainLayout />}>
          <Route path="/dashboard" element={
              <ProtectedRoute isAuthenticated={isAuthenticated()} requiredPermission="dashboard.view">
                <ModeDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partners"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="partners.view"
                allowedModes={['partners']}
              >
                <PartnersDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="blog.view"
                allowedModes={['blog']}
              >
                <BlogListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog/new"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="blog.create"
                allowedModes={['blog']}
              >
                <BlogEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/blog/:postId"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="blog.update"
                allowedModes={['blog']}
              >
                <BlogEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/dashboard"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="crm.view"
                allowedModes={['sales']}
              >
                <SalesDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/pipeline"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="pipeline.view"
                allowedModes={['sales']}
              >
                <SalesPipelinePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/tasks"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="tasks.view"
                allowedModes={['sales']}
              >
                <SalesTasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/accounts-to-activate"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="crm.view"
                allowedModes={['sales']}
              >
                <SalesAccountsToActivatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/promo"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="promo.view"
                allowedModes={['sales']}
              >
                <SalesPromoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/reports"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="sales_reports.view"
                allowedModes={['sales']}
              >
                <SalesReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/companies"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="crm.view"
                allowedModes={['sales']}
              >
                <SalesCompaniesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/company/:companyId"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="crm.view"
                allowedModes={['sales']}
              >
                <SalesCompanyProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/opportunity/:opportunityId"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="crm.view"
                allowedModes={['sales']}
              >
                <SalesOpportunityDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  allowedModes={['gestion']}
                >
                  <Agents />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/posts"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  allowedModes={['gestion']}
                >
                  <Products />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/stock"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="stock.view"
                  allowedModes={['gestion']}
                >
                  <Stock />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/stock/:stockId"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="stock.view"
                  allowedModes={['gestion']}
                >
                  <Stock />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/questions"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="questions.view"
                  allowedModes={['gestion']}
                >
                  <Questions />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/questions/:questionId"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="questions.view"
                  allowedModes={['gestion']}
                >
                  <Questions />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/sales"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="sales.view"
                  allowedModes={['gestion']}
                >
                  <Sales />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/sales/:saleId"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="sales.view"
                  allowedModes={['gestion']}
                >
                  <Sales />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/analisis"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="analisis.view"
                  allowedModes={['gestion']}
                >
                  <Analisis />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/claims"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="claims.view"
                  allowedModes={['gestion']}
                >
                  <Claims />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="calendar.view"
                  allowedModes={['gestion']}
                >
                  <CalendarModule />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/activity"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="activity.view"
                  allowedModes={['gestion']}
                >
                  <Activity />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="messages.view_internal"
                  allowedModes={['gestion']}
                >
                  <Messages />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/mercadolibre-messages"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="messages.view_mercadolibre"
                  allowedModes={['gestion']}
                >
                  <MercadoLibreMessages />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <LazyRoute>
                <ProtectedRoute isAuthenticated={isAuthenticated()} allowedModes={['gestion']}>
                  <Integrations />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/price-calculator"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="price_calculator.view"
                  allowedModes={['gestion']}
                >
                  <PriceCalculator />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/dev-testing"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated()}
                requiredPermission="dev_testing.view"
                allowedModes={['gestion']}
              >
                <DevTesting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="users.view"
                  allowedModes={['gestion']}
                >
                  <Users />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/profiles"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="profiles.view"
                  allowedModes={['gestion']}
                >
                  <Profiles />
                </ProtectedRoute>
              </LazyRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  requiredPermission="subscription.view"
                  allowedModes={['gestion']}
                >
                  <Subscription />
                </ProtectedRoute>
              </LazyRoute>
            }
          />

          <Route
            path="/super-admin"
            element={
              <LazyRoute>
                <ProtectedRoute
                  isAuthenticated={isAuthenticated()}
                  allowedModes={['superadmin']}
                  requireSuperAdmin
                >
                  <SuperAdmin />
                </ProtectedRoute>
              </LazyRoute>
            }
          />

          <Route
            path="/posts/details"
            element={
              <LazyRoute>
                <ProtectedRoute isAuthenticated={isAuthenticated()}>
                  <PostProductDetails />
                </ProtectedRoute>
              </LazyRoute>}
          />
          <Route path="/posts/add" element={
            <LazyRoute>
              <ProtectedRoute isAuthenticated={isAuthenticated()}>
                <AddProduct />
              </ProtectedRoute>
            </LazyRoute>} />
          <Route
            path="/posts/details/:productId"
            element={
              <LazyRoute>
                <ProtectedRoute isAuthenticated={isAuthenticated()}>
                  <PostProductDetails />
                </ProtectedRoute>
              </LazyRoute>}
          />
        </Route>
        //:
      )}
      <Route element={<WizardAuth />}>
        <Route
          path="/wizard"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated()}>
              <Wizard validation={true} />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Routes used by app components (profile dropdown, notifications, billing) */}
      <Route element={<MainLayout />}>
        <Route path="social/notifications" element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated()}
            requiredAnyPermissions={[
              'notifications.view_internal',
              'notifications.view_mercadolibre',
              'notifications.view_questions'
            ]}
          >
            <Notifications />
          </ProtectedRoute>} />
        <Route path="user/profile" element={
          <ProtectedRoute isAuthenticated={isAuthenticated()}>
            <Profile />
          </ProtectedRoute>} />
        <Route path="user/settings" element={
          <ProtectedRoute isAuthenticated={isAuthenticated()}>
            <Settings />
          </ProtectedRoute>} />
        <Route path="e-commerce/billing" element={
          <ProtectedRoute isAuthenticated={isAuthenticated()}>
            <Billing />
          </ProtectedRoute>} />
        <Route path="e-commerce/shopping-cart" element={
          <ProtectedRoute isAuthenticated={isAuthenticated()}>
            <ShoppingCart />
          </ProtectedRoute>} />
      </Route>

      {/* <Navigate to="/errors/404" /> */}
      <Route path="*" element={<Navigate to="/errors/404" replace />} />
    </Routes>
  );
};

export default FalconRoutes;
